import type { PropsWithChildren } from 'react';
import React from 'react';
import {
	// eslint-disable-next-line graphql-relay-compat/no-import-apollo-functions -- Read http://go/cfe-no-import-apollo-functions
	useQuery,
} from '@apollo/react-hooks';

import type { QueryResult } from 'react-apollo';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import type { ApolloErrorInterop } from '@confluence/apollo-relay-interop/entry-points/ApolloErrorInterop';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution';
import { isUnauthorizedError } from '@confluence/error-boundary/entry-points/isUnauthorizedError';
import { isNotFoundError } from '@confluence/error-boundary/entry-points/isNotFoundError';
import { fg } from '@confluence/feature-gating';
import { usePageContentId, usePageSpaceKey, usePageState } from '@confluence/page-context';
import { Redirection } from '@confluence/route-manager/entry-points/Redirection';
import { isNotFoundSpaceError } from '@confluence/content-unified-query/entry-points/errors';
import type { Route } from '@confluence/route/entry-points/Route';
import { getMonitoringClient } from '@confluence/monitoring/entry-points/getMonitoringClient';
import { ssrPermissionsErrorHandler } from '@confluence/ssr-utilities/entry-points/SSRPermissionsErrorHandler';
import { useIsTransition } from '@confluence/route-manager/entry-points/RouteState';

import { NotFoundContainer } from './NotFoundContainer';
import { ContentExpansionContainer } from './styles';
import { UnauthorizedScreen } from './UnauthorizedScreen';
import { ContentRedirect, type ContentRedirectProps } from './ContentRedirect';
import { ContentPrerequisitesQuery } from './ContentPrerequisitesQuery.graphql';
import type {
	ContentPrerequisitesQuery as ContentPrerequisitesQueryType,
	ContentPrerequisitesQueryVariables,
} from './__types__/ContentPrerequisitesQuery';
import { TimeoutErrorScreen } from './TimeoutErrorScreen';

type ContentNodeType = {
	space: {
		key: string | null;
		alias: string | null;
	} | null;
};

type ContentQueryDataType = {
	content: {
		nodes: Array<ContentNodeType | null> | null;
	} | null;
};

type ContentQueryResultType = Pick<QueryResult<ContentQueryDataType>, 'data' | 'error' | 'loading'>;

type ContentPrerequisitesParams = {
	contentId?: string;
	spaceKey?: string;
	contentQueryResult?: ContentQueryResultType;
	handleUnexpectedError?: (error: Error) => void;
	handleTimeoutError?: (error: Error) => void;

	skip?: boolean;
};

type ContentPrerequisitesResult = {
	isLoading?: boolean;
	isNotFound?: boolean;
	isRedirect?: boolean;
	isRestricted?: boolean;
	isUnexpectedError?: boolean;
	isTimeoutError?: boolean;

	// Set when `isRedirect` is true and specifies the space key to redirect to
	redirectSpaceKey?: string | null;
	redirectReason?: string;

	// Set when error is propagated from ContentQuery or from ContentPrerequisitesQuery
	error?: ApolloErrorInterop;
};

const useContentPrerequisites = ({
	spaceKey,
	contentId,
	contentQueryResult,
	handleUnexpectedError,
	handleTimeoutError,
	skip,
}: ContentPrerequisitesParams): ContentPrerequisitesResult => {
	// Redirect legacy url which doesn't have spaceKey
	const missingSpaceKey = !spaceKey;
	const missingContentId = !contentId;

	// Content is considered missing when we attempted to use contentQueryResult but no data
	// is available due to known or unknown errors. This is only applicable for SSR.
	// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
	const missingContentSSR = process.env.REACT_SSR && contentQueryResult && !contentQueryResult.data;

	// We need to check for "data" or "error" existence in contentQueryResult and not just the existence of contentQueryResult,
	// since this object is the query result from Apollo and it might be present but with no data or error such as { variables: fn, refetch: fn }
	const contentQueryResultCheck = fg('use_content_query_result_data_error')
		? contentQueryResult?.data || contentQueryResult?.error
		: contentQueryResult;

	const skipContentPrerequisites =
		skip || missingContentId || (Boolean(contentQueryResultCheck) && !missingContentSSR);

	const contentPrerequisitesQueryResult = useQuery<
		ContentPrerequisitesQueryType,
		ContentPrerequisitesQueryVariables
	>(
		// eslint-disable-next-line graphql-relay-compat/no-import-graphql-operations -- Preloaded GraphQL Operation
		ContentPrerequisitesQuery,
		{
			errorPolicy: 'all',
			variables: {
				contentId: contentId!,
				missingSpaceKey,
				spaceKey,
			},
			skip: skipContentPrerequisites,
		},
	);

	const { createAnalyticsEvent } = useAnalyticsEvents();

	const {
		data,
		error,
		loading: queryLoading,
	} = skipContentPrerequisites ? contentQueryResult || {} : contentPrerequisitesQueryResult;

	const [{ contentIdLoading }] = usePageState();

	const loading = queryLoading || contentIdLoading;

	if ((!data || missingContentId) && loading) {
		// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
		if (process.env.REACT_SSR && fg('log_view_page_content_not_ssred')) {
			getMonitoringClient().submitError(
				{
					message: 'ContentPrerequisites: still loading',
					contentId,
					skip,
					queryLoading,
					contentIdLoading,
					hadContentQueryData: Boolean(contentQueryResult?.data),
					hadPrerequisitesQueryData: Boolean(contentPrerequisitesQueryResult?.data),
					missingContentSSR,
				},
				{
					attribution: Attribution.BACKBONE,
				},
			);
		}
		return { isLoading: true };
	}

	// Known Apollo bug: Errors are not saved within Apollo in-memory cache
	// https://github.com/apollographql/apollo-client/issues/4806. For restricted spaces and pages,
	// the 403 error is subsequently undefined after retrieval attempt. Restricted spaces means
	// content will be null, but the error might be undefined.
	const isEmptyContent = !loading && !error && data && !data?.content;
	const isTimeoutError = error?.toString().includes('SocketTimeoutException');

	if ((error && isUnauthorizedError(error)) || isEmptyContent) {
		if (error) markErrorAsHandled(error);
		// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
		if (isEmptyContent && !process.env.REACT_SSR) {
			createAnalyticsEvent({
				type: 'sendOperationalEvent',
				data: {
					source: 'ContentPrerequisites',
					action: 'returnedEmpty',
					actionSubject: 'content',
					attributes: {
						contentId,
						skip,
						hadContentQueryData: Boolean(contentQueryResult?.data),
					},
				},
			}).fire();
		}

		return {
			error,
			isRestricted: true,
		};
	}

	// Catch all for errors, no content and calls that return null/undefined
	// If FF is on, this catches Not Found errors (404) and other errors are handled below
	const content = data?.content?.nodes || [];
	const shouldHandleErrors = !!error;

	if (
		missingContentId ||
		(content.length === 0 && !shouldHandleErrors) ||
		(shouldHandleErrors && isNotFoundError(error))
	) {
		return {
			isNotFound: true,
		};
	}
	const expectedSpaceKey = content[0]?.space?.alias || content[0]?.space?.key;
	// Checks for case where old / wrong spacekey is provided causing reload
	// on SSR -> SPA transition once the ContentRedirectQuery resolves.
	// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
	const wrongSpaceKeyOnSSR = process.env.REACT_SSR && expectedSpaceKey !== spaceKey;

	const notFoundSpaceError = error && isNotFoundSpaceError(error);

	if (missingSpaceKey || wrongSpaceKeyOnSSR || notFoundSpaceError) {
		markErrorAsHandled(error);
		return {
			isRedirect: true,
			redirectSpaceKey: expectedSpaceKey,
			redirectReason: `CP-noSK:${missingSpaceKey}|wrongSK:${Boolean(wrongSpaceKeyOnSSR)}|noSpace:${Boolean(notFoundSpaceError)}`,
		};
	}

	if (error && handleTimeoutError && isTimeoutError) {
		handleTimeoutError(error);
		return {
			error,
			isTimeoutError: true,
		};
	}

	if (error && handleUnexpectedError) {
		handleUnexpectedError(error);
		return {
			error,
			isUnexpectedError: true,
		};
	}

	return {};
};

export type ContentPrerequisitesProps = PropsWithChildren<
	{
		route: Route;
		contentQueryResult?: ContentQueryResultType;
		passThroughOnLoading?: boolean;
		handleUnexpectedError?: (error: Error) => void;
		handleTimeoutError?: (error: Error) => void;
	} & Pick<ContentRedirectProps, 'versionOverride'>
>;

/**
 * The common error and restrictions handling component for all content types.
 *
 * Newer content types can create their own prerequisites component to define their component composition
 * or custom error handling paths, and leverage `ContentPrerequisites` to handle the common error cases.
 *
 * @param route - the current route object
 * @param contentQueryResult - the content query result from the parent component e.g. ContentUnifiedQuery
 * @param passThroughOnLoading - whether to render null or pass through the component and let itself handle
 * the loading state when query is loading
 */
export const ContentPrerequisites = ({
	children,
	route,
	contentQueryResult,
	passThroughOnLoading = false,
	handleUnexpectedError,
	handleTimeoutError,
	versionOverride,
}: ContentPrerequisitesProps) => {
	const [contentId] = usePageContentId();
	const [spaceKey] = usePageSpaceKey();
	const isTransition = useIsTransition();

	const prerequisites = useContentPrerequisites({
		spaceKey,
		contentId,
		contentQueryResult,
		handleUnexpectedError,
		handleTimeoutError,
	});
	if (prerequisites.isNotFound) {
		return <NotFoundContainer route={route} spaceKey={spaceKey} contentId={contentId} />;
	}

	const getContentComponent = () => {
		if (!isTransition && contentId && ssrPermissionsErrorHandler.getRestrictedRoute() === 'view') {
			return (
				<UnauthorizedScreen
					contentId={contentId}
					accessType={ssrPermissionsErrorHandler.getErrorType()}
				/>
			);
		}

		if (prerequisites.isLoading) {
			return passThroughOnLoading ? (
				<ContentExpansionContainer>{children}</ContentExpansionContainer>
			) : null;
		}

		if (contentId && prerequisites.isRestricted) {
			return (
				<UnauthorizedScreen contentId={contentId} accessType={getAccessType(prerequisites.error)} />
			);
		}

		if (prerequisites.isRedirect) {
			return (
				<Redirection
					name={route.name}
					reason={prerequisites.redirectReason}
					params={{
						contentId,
						spaceKey: prerequisites.redirectSpaceKey,
					}}
				/>
			);
		}

		if (prerequisites.isTimeoutError) {
			return <TimeoutErrorScreen contentId={contentId} spaceKey={spaceKey} />;
		}

		return <ContentExpansionContainer>{children}</ContentExpansionContainer>;
	};

	return (
		<>
			<ContentRedirect versionOverride={versionOverride} />
			{getContentComponent()}
		</>
	);
};

function getAccessType(error: ApolloErrorInterop | undefined): 'inherited' | 'view' {
	return error?.graphQLErrors?.[0]?.extensions?.data?.errors?.[0]?.message?.key ===
		'confluence.content.restricted.inherited'
		? 'inherited'
		: 'view';
}
